/**
 * What a known thing is, and what the account of how it became known is.
 *
 * The domain-free half of what used to be `sme-engine/types.ts`. Nothing here
 * names a risk, a cover, a business or a household: a fact is a value with a
 * source and an age, and that is true of a company's payroll and of a family's
 * monthly expenses in exactly the same way.
 *
 * The test for whether something belongs here is whether a second product could
 * ever want it. The ladder that ranks a typed answer above a connected system,
 * yes. What the answer is about, no.
 */

/**
 * Where a fact came from. This is what the engine ranks, not the question it
 * answers. The same question answered by a payroll feed outranks the same
 * question answered from memory a year ago.
 */
export const FACT_SOURCES = ['USER', 'DOCUMENT', 'CONNECTOR', 'MODEL'] as const
export type FactSource = (typeof FACT_SOURCES)[number]

/**
 * Rule priority. Lower number wins.
 *
 * Inference can never produce the strongest status, which is enforced by
 * `capRank` in merge.ts rather than by anybody remembering.
 */
export const SOURCE_RANK = {
  DIRECT_ANSWER: 1,
  VERIFIED_DOCUMENT: 2,
  DETERMINISTIC_CALCULATION: 3,
  INDUSTRY_RULE: 4,
  INFERENCE: 5,
  /**
   * Not a source at all, but the absence of one. Ranked last so a missing answer
   * never displaces something actually known, while its follow-up question
   * still surfaces alongside whatever did decide the status.
   */
  ABSENCE: 6,
} as const
export type SourceRank = (typeof SOURCE_RANK)[keyof typeof SOURCE_RANK]

/**
 * How an assertion was arrived at. Distinct from a fact's own source label: a
 * rule can derive an industry pattern from a fact the user stated directly, and
 * it is the derivation that is weak, not the fact.
 */
export const RANK_LABEL: Record<SourceRank, string> = {
  1: 'a direct answer',
  2: 'a verified document or connected system',
  3: 'a calculation',
  4: 'an industry pattern',
  5: 'an inference',
  6: 'the absence of an answer',
}

/** Default rank per source. A model can never enter above inference. */
export const DEFAULT_RANK: Record<FactSource, SourceRank> = {
  USER: 1,
  DOCUMENT: 2,
  CONNECTOR: 2,
  MODEL: 5,
}

export type Confidence = 'HIGH' | 'MEDIUM' | 'LOW'

/**
 * Where in a document a value was read.
 *
 * `verbatim` is the point. A page number is a claim about a document; the exact
 * text is checkable against it. An extractor that cannot produce a span that
 * literally occurs in the source has not read anything, whatever confidence it
 * reports about itself, so this is what a trust rule can actually test.
 */
export interface Citation {
  /** Filename or label of the document the value was read from. */
  document: string
  page: number | null
  clause?: string
  verbatim: string
}

/** The system behind a CONNECTOR fact. */
export interface Origin {
  ref: string
  label?: string
}

/**
 * A single known thing, with provenance and an age.
 *
 * Rules read `value` and pass `rank` straight through, so a rule never needs to
 * know whether it is looking at a form answer or a bank feed.
 */
export interface Fact<T> {
  value: T
  source: FactSource
  rank: SourceRank
  /** ISO timestamp of when this was observed to be true. */
  observed_at: string
  /** ISO timestamp after which it is no longer treated as current. */
  stale_after?: string
  /** Where it was read, for a DOCUMENT fact. Absent for anything typed. */
  citation?: Citation
  /**
   * Which system reported it, for a CONNECTOR fact. Absent for anything typed.
   *
   * The same job `citation` does for a document, and carried for the same
   * reason: a figure is only as good as the account of where it came from, and
   * "from a connected system" is not an account. `ref` is the connection, so
   * two payroll systems reporting the same field stay distinguishable; `label`
   * is how its customer would name it, so a reader sees "Gusto" rather than an
   * identifier.
   *
   * No rule reads it. It is what lets the record say who said what, and it is
   * what the account of a reading is derived from rather than stored twice.
   */
  origin?: Origin
}

/** Build a fact. `observed_at` defaults to now, so callers stay terse. */
export function factOf<T>(
  value: T,
  opts: {
    source?: FactSource
    rank?: SourceRank
    observed_at?: string
    stale_after?: string
    origin?: Origin
  } = {},
): Fact<T> {
  const source = opts.source ?? 'USER'
  return {
    value,
    source,
    rank: opts.rank ?? DEFAULT_RANK[source],
    observed_at: opts.observed_at ?? new Date().toISOString(),
    ...(opts.stale_after ? { stale_after: opts.stale_after } : {}),
    ...(opts.origin ? { origin: opts.origin } : {}),
  }
}

export function valueOf<T>(f: Fact<T> | null | undefined): T | null {
  return f ? f.value : null
}

/**
 * Which of two facts about the same thing to keep.
 *
 * A record holds one fact per field, so this is the write-side rule: when a
 * connector or document reports something the user already answered, rank wins
 * first and recency breaks the tie. A fresh guess never displaces a verified
 * reading, and a stale verified reading never displaces a fresher one.
 */
export function preferFact<T>(a: Fact<T> | null, b: Fact<T> | null): Fact<T> | null {
  if (!a) return b
  if (!b) return a
  if (a.rank !== b.rank) return a.rank < b.rank ? a : b
  return Date.parse(a.observed_at) >= Date.parse(b.observed_at) ? a : b
}

/**
 * How a recorded value came to be known.
 *
 * Narrower than `Fact` and used for the parts of a document rather than for an
 * answer: every value read off a schedule carries one, so that an extracted
 * figure and a corrected one are distinguishable after the fact. USER outranks
 * DOCUMENT for the same reason a direct answer outranks a document in the
 * exposure engine: a person who has looked at the schedule and disagreed with
 * what was read from it is the better source.
 */
export interface Provenance {
  source: 'USER' | 'DOCUMENT'
  observed_at: string
  /**
   * Where the value was read.
   *
   * On DOCUMENT it is the evidence for the value, and one without it is
   * unverifiable. On USER it is what the document said before somebody
   * disagreed, kept so the disagreement stays visible: a reading that simply
   * vanishes on the first edit leaves no record that anything was overruled.
   */
  citation?: Citation
}

/**
 * A person's own answer, carrying what it replaced.
 *
 * The citation is passed on rather than dropped, because "the schedule says X
 * and the reader says Y" is a more useful record than "the reader says Y". It
 * changes nothing about precedence: USER still outranks DOCUMENT, and nothing
 * in the comparison reads a citation at all.
 */
export function statedByUser(observed_at: string, superseded?: Citation): Provenance {
  return superseded
    ? { source: 'USER', observed_at, citation: superseded }
    : { source: 'USER', observed_at }
}

/**
 * Nobody has established this yet.
 *
 * Distinct from an empty list, which asserts that none apply. Defaulting the
 * unknown to the empty list is what would let an unread schedule read as a
 * clean one, so the two are kept apart at the type level.
 */
export const UNKNOWN = 'UNKNOWN' as const
export type Unknown = typeof UNKNOWN
